import React, { useState } from "react";
import { format, addMonths } from "date-fns";
import AmountInput from "../../../../../components/JewelleryPlans/common/AmountInput";
import { CalendarIcon } from "@heroicons/react/outline";

const PlanConfiguration = ({ data, onNext, onBack }) => {
  const [monthlyAmount, setMonthlyAmount] = useState(data.monthlyAmount || "");
  const [startDate, setStartDate] = useState(data.startDate || new Date());
  const [notes, setNotes] = useState(data.notes || "");
  const [errors, setErrors] = useState({});

  const template = data.template;
  const maturityDate = template
    ? addMonths(startDate, template.tenureMonths)
    : null;

  const calculateMaturityValue = () => {
    if (!monthlyAmount || !template) return 0;

    const totalPayments = monthlyAmount * template.tenureMonths;

    // Add benefits if applicable
    if (template.benefitType === "FREE_INSTALLMENT") {
      const freeInstallments = template.benefitParams?.freeInstallments || 1;
      return totalPayments + monthlyAmount * freeInstallments;
    }

    return totalPayments;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!monthlyAmount) {
      newErrors.monthlyAmount = "Monthly amount is required";
    } else if (monthlyAmount < template.minInstallment) {
      newErrors.monthlyAmount = `Minimum amount is ₹${template.minInstallment}`;
    } else if (monthlyAmount > template.maxInstallment) {
      newErrors.monthlyAmount = `Maximum amount is ₹${template.maxInstallment}`;
    }

    if (!startDate) {
      newErrors.startDate = "Start date is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProceed = () => {
    if (validateForm()) {
      onNext({
        monthlyAmount,
        startDate,
        notes,
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-text mb-4">
          Configure Plan Details
        </h2>

        <div className="space-y-4">
          {/* Monthly Amount */}
          <AmountInput
            label="Monthly Installment Amount"
            value={monthlyAmount}
            onChange={setMonthlyAmount}
            min={template?.minInstallment}
            max={template?.maxInstallment}
            error={errors.monthlyAmount}
            helpText={`Amount between ₹${template?.minInstallment} - ₹${template?.maxInstallment}`}
            required
          />

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-text">
              Plan Start Date <span className="text-danger">*</span>
            </label>
            <div className="mt-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <CalendarIcon className="h-5 w-5 text-neutral-dark" />
              </div>
              <input
                type="date"
                value={format(startDate, "yyyy-MM-dd")}
                onChange={(e) => setStartDate(new Date(e.target.value))}
                min={format(new Date(), "yyyy-MM-dd")}
                className={`block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm 
                  focus:ring-primary focus:border-primary sm:text-sm
                  ${
                    errors.startDate
                      ? "border-danger-light"
                      : "border-neutral-light"
                  }`}
              />
            </div>
            {errors.startDate && (
              <p className="mt-1 text-sm text-danger">{errors.startDate}</p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-text">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="mt-1 block w-full border-neutral-light rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              placeholder="Any special instructions or notes..."
            />
          </div>
        </div>
      </div>

      {/* Plan Summary */}
      {monthlyAmount && template && (
        <div className="bg-neutral-light rounded-lg p-4">
          <h3 className="text-sm font-medium text-text mb-3">Plan Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-neutral-dark">Template:</span>
              <span className="font-medium">{template.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-dark">Monthly Amount:</span>
              <span className="font-medium">
                ₹{Number(monthlyAmount).toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-dark">Tenure:</span>
              <span className="font-medium">
                {template.tenureMonths} months
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-dark">Start Date:</span>
              <span className="font-medium">
                {format(startDate, "dd MMM yyyy")}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-dark">Maturity Date:</span>
              <span className="font-medium">
                {maturityDate && format(maturityDate, "dd MMM yyyy")}
              </span>
            </div>
            <div className="pt-2 mt-2 border-t border-neutral-light">
              <div className="flex justify-between">
                <span className="text-text font-medium">Maturity Value:</span>
                <span className="text-lg font-semibold text-success">
                  ₹{calculateMaturityValue().toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Schedule Preview */}
      <div className="bg-secondary-light border border-secondary rounded-lg p-4">
        <h4 className="text-sm font-medium text-secondary-dark mb-2">
          Payment Schedule
        </h4>
        <div className="text-xs text-secondary-dark space-y-1">
          <p>• First payment due on: {format(startDate, "dd MMM yyyy")}</p>
          <p>• Monthly payment: ₹{monthlyAmount || 0}</p>
          <p>• Total {template?.tenureMonths || 0} payments</p>
          <p>
            • Grace period: {template?.graceDays || 7} days for each payment
          </p>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-4 py-2 border border-neutral-light rounded-md text-sm font-medium text-text bg-background hover:bg-neutral-light"
        >
          Back
        </button>
        <button
          onClick={handleProceed}
          className="px-6 py-2 bg-primary text-text-on-primary rounded-md text-sm font-medium hover:bg-primary-dark"
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default PlanConfiguration;
